import { useEffect, useMemo, useState } from 'react'
import type { RfqReviewLine } from './review-domain'
import {
  type AnalysisView,
  type PartAnalysisState,
  REDUCED_MOTION_HOLD_MS,
  buildAnalysisPlan,
  deriveAnalysisView,
  terminalStateForLine,
} from './rfq-analysis'

/**
 * UID-261 — run-once-per-draft guard. `RfqReviewPage` remounts on every
 * navigation to `/rfq/new` (UID-112/UID-119's design — there is no
 * "freshly generated" flag on the bundle), so a plain `useRef` inside the
 * hook can't tell "first time seeing this draft" from "revisiting it" —
 * it resets on every remount along with the rest of the component's state.
 * This module-level `Set` survives remounts within the same app session,
 * giving the guard the same "sticks around above the page" lifetime
 * `rfqBundle` itself has, without touching `rfq-bundle-context.tsx`.
 *
 * UID-570 — the original version of this guard only lived in this in-memory
 * `Set`, resetting on every hard page reload; a comment here used to defend
 * that as "the correct boundary" (a reload is a genuinely fresh session).
 * In practice that meant a draft resumed from the "My RFQs" Draft tab, or a
 * `/rfq/new` page simply refreshed, replayed the FULL "Preparing your RFQ"
 * animation every time, indistinguishable from a brand-new RFQ arriving
 * from uploads/catalog — which is the one case the animation is actually
 * for. Backed the guard with `localStorage` (same fail-open pattern as
 * `bulk-cart-warn-preference.ts`) below so a draft, once analyzed, stays
 * "seen" across reloads and future sessions, not just remounts.
 *
 * Keyed on a stable fingerprint of the analyzed line ids (`draftKeyFromLines`
 * below), NOT on `rfqBundle.draftRfqId` — that id is minted asynchronously
 * (the eager-create `POST /rfq` effect in `RfqReviewPage`) and is often still
 * `undefined` at the exact moment this hook needs to decide whether to
 * animate, so it can't be read synchronously at mount. The real domain has
 * no other synchronous "draft identity" available this early; the line-id
 * fingerprint is the honest stand-in the ticket's "or a stable draft key"
 * wording calls for. One consequence, called out plainly: revisiting the
 * SAME set of parts never replays the animation, but a draft that grew
 * (more parts added since first analyzed) gets a fresh run keyed to its new,
 * larger fingerprint — a reasonable reading of "per draft" given the real
 * domain's constraints, not a literal `rfqId` match.
 */
const analyzedDraftKeys = new Set<string>()

/** UID-570 — durable half of the guard. Persists analyzed draft-key
 * fingerprints across reloads/sessions. FAIL-OPEN (same convention as
 * `bulk-cart-warn-preference.ts`): any `localStorage` throw (private
 * browsing, storage disabled/full) is treated as "nothing persisted" —
 * worst case the animation replays once more, never a crash. Bounded to the
 * most recent `MAX_PERSISTED_KEYS` fingerprints (oldest evicted first) so a
 * long-lived browser profile can't grow this without bound. */
const ANALYZED_DRAFT_KEYS_STORAGE_KEY = 'ff_rfq_analyzed_draft_keys'
const MAX_PERSISTED_KEYS = 50

function loadPersistedAnalyzedKeys(): readonly string[] {
  try {
    const raw = window.localStorage.getItem(ANALYZED_DRAFT_KEYS_STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter((entry) => typeof entry === 'string') : []
  } catch {
    return []
  }
}

function persistAnalyzedKey(key: string): void {
  try {
    const existing = loadPersistedAnalyzedKeys()
    if (existing.includes(key)) return
    const next = [...existing, key].slice(-MAX_PERSISTED_KEYS)
    window.localStorage.setItem(ANALYZED_DRAFT_KEYS_STORAGE_KEY, JSON.stringify(next))
  } catch {
    // Intentionally swallowed — see doc comment above.
  }
}

/** Test-only escape hatch — clears both halves of the guard (the in-memory
 * `Set` and its persisted `localStorage` mirror) so test files that render
 * multiple RFQ drafts sharing fixture line ids (a very common pattern in
 * this codebase's fixtures) don't leak "already analyzed" state between
 * `it()` blocks. Not used by any production code path. */
export function resetRfqAnalysisGuardForTests(): void {
  analyzedDraftKeys.clear()
  try {
    window.localStorage.removeItem(ANALYZED_DRAFT_KEYS_STORAGE_KEY)
  } catch {
    // Storage disabled in this test environment — nothing to clear.
  }
}

function draftKeyFromLines(lines: readonly RfqReviewLine[]): string {
  return lines
    .map((line) => line.id)
    .slice()
    .sort()
    .join('|')
}

function terminalStatesFor(lines: readonly RfqReviewLine[]): Map<string, PartAnalysisState> {
  const states = new Map<string, PartAnalysisState>()
  for (const line of lines) states.set(line.id, terminalStateForLine(line))
  return states
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

export interface UseRfqAnalysisResult extends AnalysisView {
  /** Whether the overlay should be rendered at all. */
  readonly active: boolean
}

/**
 * Drives the "Preparing your RFQ" entry animation (UID-261). Snapshots
 * `lines` ONCE, at mount (`_startRfqAnalysis(list)`'s own snapshot
 * semantics, `.dc.html` L6470/L6474) — parts added to the table later in the
 * same visit (e.g. via "Add parts" while already reviewing) are not swept
 * into a fresh run; only a genuine remount with a changed line set gets a
 * new schedule (see the guard's doc comment above). Zero lines at mount
 * means the overlay never activates for this mount, per the ticket.
 *
 * UID-544 — the run is self-dismissing and bounded: `buildAnalysisPlan`
 * animates at most `ANIMATED_PART_COUNT` lines and guarantees
 * `dismissDelayMs <= ANALYSIS_BUDGET_MS` (2s) for any bundle size, so there
 * is no user-facing skip control to expose (ADR-0166 reverses ADR-0089 D5).
 */
export function useRfqAnalysis(lines: readonly RfqReviewLine[]): UseRfqAnalysisResult {
  const [initialLines] = useState(() => lines)
  const draftKey = useMemo(() => draftKeyFromLines(initialLines), [initialLines])
  const alreadyRan =
    initialLines.length === 0 ||
    analyzedDraftKeys.has(draftKey) ||
    loadPersistedAnalyzedKeys().includes(draftKey)
  const plan = useMemo(() => buildAnalysisPlan(initialLines), [initialLines])

  const [statesByLineId, setStatesByLineId] = useState<Map<string, PartAnalysisState>>(() =>
    alreadyRan ? terminalStatesFor(initialLines) : new Map(),
  )
  const [active, setActive] = useState(!alreadyRan)

  // Mount-only by design: `initialLines`/`draftKey`/`plan`/`alreadyRan` are
  // all derived once (lazy state / memo over that same lazy state) and never
  // change identity across this hook instance's lifetime, so there is
  // nothing meaningful to add to the dependency array.
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only schedule; initialLines/draftKey/plan/alreadyRan are all derived once and never change identity for this hook instance.
  useEffect(() => {
    if (alreadyRan) return
    analyzedDraftKeys.add(draftKey)
    persistAnalyzedKey(draftKey)

    if (prefersReducedMotion()) {
      setStatesByLineId(terminalStatesFor(initialLines))
      const holdTimer = window.setTimeout(() => setActive(false), REDUCED_MOTION_HOLD_MS)
      return () => window.clearTimeout(holdTimer)
    }

    const timers = plan.transitions.map((transition) =>
      window.setTimeout(() => {
        setStatesByLineId((prev) => {
          const next = new Map(prev)
          next.set(transition.lineId, transition.state)
          return next
        })
      }, transition.delayMs),
    )
    timers.push(window.setTimeout(() => setActive(false), plan.dismissDelayMs))

    return () => {
      for (const timer of timers) window.clearTimeout(timer)
    }
  }, [])

  const view = deriveAnalysisView(initialLines, statesByLineId)

  return { ...view, active }
}
